import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Trophy, CheckCircle2, Sparkles, ArrowRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import WeKitLogo from "@/components/WeKitLogo";
import ShareBar from "@/components/ShareBar";
import { supabase } from "@/integrations/supabase/client";
import { deriveBadges, buildSchedule, type Milestone, formatMonth } from "@/lib/journey";
import { isExternalAvatarUrl } from "@/lib/avatar";

type PublicProfile = {
  id: string;
  handle: string;
  first_name: string;
  avatar_url: string | null;
  portfolio_tagline: string | null;
  joined_at: string;
  total_xp: number;
  current_streak: number;
  longest_streak: number;
};

export default function Portfolio() {
  const { handle } = useParams();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!handle) return;
    (async () => {
      const { data: pf } = await supabase
        .from("public_portfolios")
        .select("*")
        .ilike("handle", handle)
        .maybeSingle();
      if (!pf) { setNotFound(true); setLoading(false); return; }
      setProfile(pf as PublicProfile);
      const { data: ms } = await (supabase.rpc as any)(
        "get_public_journey_milestones",
        { _user_id: (pf as any).id },
      );
      setMilestones((ms ?? []) as Milestone[]);
      setLoading(false);
    })();
  }, [handle]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading portfolio…</div>;
  }
  if (notFound || !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="font-display text-3xl font-bold">Portfolio not found</h1>
        <p className="text-muted-foreground">This portfolio is private or doesn't exist.</p>
        <Button asChild><Link to="/">Start your own WCC360 journey</Link></Button>
      </div>
    );
  }

  const steps = buildSchedule(
    { id: "", plan: "individual", started_at: profile.joined_at, expires_at: new Date(new Date(profile.joined_at).getTime() + 365 * 86400000).toISOString(), is_active: true },
    milestones,
  );
  const badges = deriveBadges(steps);
  const completed = steps.filter((s) => s.status === "completed").length;
  const earnedBadges = badges.filter((b) => b.earned);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5">
      <header className="border-b border-border/60 bg-background/85 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/"><WeKitLogo size="sm" /></Link>
          <Button asChild size="sm" variant="outline">
            <Link to="/">Start your journey <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row items-start sm:items-center gap-5 mb-10"
        >
          <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-3xl bg-primary/15 flex items-center justify-center overflow-hidden border border-border">
            {isExternalAvatarUrl(profile.avatar_url ?? "") ? (
              <img src={profile.avatar_url!} alt={profile.first_name} className="h-full w-full object-cover" />
            ) : (
              /* Stored avatars live in a private bucket a public visitor can't
                 sign for — show initials rather than a broken image. */
              <span className="text-3xl font-display font-bold text-primary">{profile.first_name[0]}</span>
            )}
          </div>
          <div className="flex-1">
            <Badge variant="outline" className="mb-2 gap-1.5">
              <Sparkles className="h-3 w-3 text-primary" /> WCC360 Portfolio
            </Badge>
            <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight">{profile.first_name}</h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              {profile.portfolio_tagline ?? "On a 365-day Future of Work growth journey."}
            </p>
            <p className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Joined {formatMonth(new Date(profile.joined_at))}
            </p>
            <div className="mt-4">
              <ShareBar
                url={typeof window !== "undefined" ? window.location.href : `https://wekit.ai/p/${profile.handle}`}
                title={`${profile.first_name}'s WCC360 Future of Work portfolio — ${profile.total_xp} XP, ${completed} milestones`}
                surface="portfolio"
              />
            </div>
          </div>
        </motion.section>

        {/* Stats */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          <Stat icon={Trophy} label="XP" value={profile.total_xp} />
          <Stat icon={Flame} label="Streak" value={profile.current_streak} suffix="d" />
          <Stat icon={CheckCircle2} label="Milestones" value={completed} total={steps.length} />
          <Stat icon={Sparkles} label="Badges" value={earnedBadges.length} total={badges.length} />
        </section>

        {/* Journey ribbon */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Journey so far</h2>
            <div className="flex gap-1.5 overflow-x-auto pb-2">
              {steps.map((s, i) => (
                <div
                  key={i}
                  className={`h-10 flex-1 min-w-6 rounded ${
                    s.status === "completed" ? "bg-primary" : "bg-muted"
                  }`}
                  title={`${s.title} · ${s.status}`}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">{completed} of {steps.length} milestones completed</p>
          </CardContent>
        </Card>

        {/* Badges */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Badges earned</h2>
            {earnedBadges.length === 0 ? (
              <p className="text-sm text-muted-foreground">No badges yet — the journey has just begun.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {earnedBadges.map((b) => (
                  <div key={b.key} className="rounded-2xl border border-border/60 bg-card p-4 text-center">
                    <div className="text-3xl mb-1">{b.emoji}</div>
                    <div className="text-sm font-semibold">{b.label}</div>
                    <div className="text-[11px] text-muted-foreground">{b.blurb}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card">
          <CardContent className="p-8 text-center space-y-3">
            <h2 className="font-display text-2xl font-bold">Your growth, your story.</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Career development is not a one-time decision. It's a lifelong journey. WCC360 grows with you.
            </p>
            <Button asChild size="lg">
              <Link to="/">Start your own WCC360 journey</Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, total, suffix }: { icon: any; label: string; value: number; total?: number; suffix?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider font-semibold mb-2">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <div className="font-display text-2xl font-bold">
          {value}{suffix ?? ""}
          {total !== undefined && <span className="text-sm text-muted-foreground font-normal"> / {total}</span>}
        </div>
      </CardContent>
    </Card>
  );
}
